import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";
import api, { money } from "@/lib/api";
import { buildInbox, nextAction } from "@/lib/inbox";
import MonthEndJourney from "@/components/MonthEndJourney";
import DashboardCoach from "@/components/DashboardCoach";
import JournalsDueModal from "@/components/JournalsDueModal";
import DashboardCustomise from "@/components/DashboardCustomise";
import { Page, PageHeader } from "@/components/Bits";
import MonthEndBar from "@/components/MonthEndBar";
import {
  CalendarCheck, Warning, ListChecks, ShieldCheck, Scales, ArrowsClockwise, Sparkle,
  Receipt, TrendUp, CurrencyGbp, Cube, ClockCounterClockwise, ArrowRight, Target, ChatCircleText, At, GraduationCap, PlayCircle, Tray, CheckCircle, CaretRight, X, Compass, SlidersHorizontal,
} from "@phosphor-icons/react";

const monthName = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};
const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const TONES = {
  navy: { chip: "bg-navy/[0.06] text-navy", val: "text-navy" },
  orange: { chip: "bg-orange/10 text-orangeink", val: "text-navy" },
  warn: { chip: "bg-warn/15 text-warn", val: "text-warn" },
  negative: { chip: "bg-negative/10 text-negative", val: "text-negative" },
  positive: { chip: "bg-positive/10 text-positive", val: "text-positive" },
};

function ActionTile({ id, icon: Icon, label, value, sub, tone = "navy", onClick, delay = 0 }) {
  const t = TONES[tone] || TONES.navy;
  return (
    <motion.button onClick={onClick} data-testid={id}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay }}
      whileHover={{ y: -3 }}
      className="text-left card-interactive p-5 group">
      <div className="flex items-center justify-between">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.chip}`}><Icon size={20} weight="fill" /></span>
        <ArrowRight size={15} className="text-slate-300 group-hover:text-orangeink group-hover:translate-x-0.5 transition-all" />
      </div>
      <div className={`font-heading font-extrabold text-3xl tracking-tight mt-4 tabular ${t.val}`}>{value}</div>
      <div className="text-sm font-medium text-navy/80 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-600 mt-0.5">{sub}</div>}
    </motion.button>
  );
}

const TYPE_META = {
  PREPAYMENT: { label: "Prepayments", icon: Receipt, to: "/prepayments" },
  ACCRUAL: { label: "Accruals", icon: TrendUp, to: "/accruals" },
  DEFERRED_INCOME: { label: "Deferred Income", icon: CurrencyGbp, to: "/deferred-income" },
  DEPRECIATION: { label: "Depreciation NBV", icon: Cube, to: "/fixed-assets" },
};

export const DASH_WIDGETS = [
  { id: "action-tiles", title: "Action tiles" },
  { id: "status-strip", title: "Status strip" },
  { id: "position-type", title: "Position by type" },
  { id: "revenue-secured", title: "Revenue secured" },
  { id: "recent-activity", title: "Recent activity" },
];
const DEFAULT_ORDER = DASH_WIDGETS.map((w) => w.id);

function normaliseLayout(saved) {
  const so = Array.isArray(saved?.order) ? saved.order.filter((x) => DEFAULT_ORDER.includes(x)) : [];
  const order = [...so, ...DEFAULT_ORDER.filter((x) => !so.includes(x))];
  const hidden = Array.isArray(saved?.hidden) ? saved.hidden.filter((x) => DEFAULT_ORDER.includes(x)) : [];
  return { order, hidden };
}

function StripCard({ id, icon: Icon, dot, title, sub, onClick, iconClass = "text-slate-600", titleAttr }) {
  return (
    <button onClick={onClick} data-testid={id} title={titleAttr}
      className="flex items-center gap-3 card-interactive px-5 py-4 text-left">
      {dot && <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />}
      <Icon size={18} className={iconClass} />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-navy">{title}</div>
        <div className="text-xs text-slate-600 truncate">{sub}</div>
      </div>
    </button>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [accuracyMeta, setAccuracyMeta] = useState(null);
  const [learningDismissed, setLearningDismissed] = useState(
    () => localStorage.getItem("lottie_dash_learning_dismissed") === "1");
  const [coachReplay, setCoachReplay] = useState(0);
  const [showDue, setShowDue] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const [layout, setLayout] = useState(() => normaliseLayout(null));
  const [layoutReady, setLayoutReady] = useState(false);
  const [guardDismissed, setGuardDismissed] = useState(false);
  const nav = useNavigate();

  const dismissGuard = () => {
    setGuardDismissed(true);
    api.post("/dashboard/guard-blocked/dismiss")
      .then(() => window.dispatchEvent(new Event("badges:refresh"))).catch(() => {});
  };

  const saveLayout = (next) => {
    setLayout(next);
    api.put("/dashboard/preferences", next).catch(() => {});
  };

  const startGuided = () => {
    sessionStorage.setItem("lottie_guided", "1");
    window.dispatchEvent(new Event("guided:start"));
  };

  const dismissLearning = () => {
    localStorage.setItem("lottie_dash_learning_dismissed", "1");
    setLearningDismissed(true);
  };
  const restoreLearning = () => {
    localStorage.removeItem("lottie_dash_learning_dismissed");
    setLearningDismissed(false);
  };

  useEffect(() => {
    api.get("/dashboard/overview").then(({ data }) => setD(data)).catch(() => setD({ has_tenant: false }));
    api.get("/accruals/recurring/variance").then(({ data }) => { setAccuracy(data.summary?.accuracy); setAccuracyMeta(data.summary || null); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (d && d.has_tenant && !layoutReady) {
      setLayout(normaliseLayout(d.dashboard_layout));
      setLayoutReady(true);
    }
  }, [d, layoutReady]);

  if (!d) return <Page><span className="font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</span></Page>;
  if (!d.has_tenant) {
    return (
      <Page><PageHeader title="Dashboard" subtitle="Connect a Xero organisation to get started." />
        <button onClick={() => nav("/onboarding")} className="bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orangedark transition-colors" data-testid="connect-cta">Start setup</button>
      </Page>
    );
  }

  const recon = d.reconciliation || {};
  const reconOk = recon.variances === 0 && recon.total > 0;
  const chartData = (d.revenue_forecast || []).map((r) => ({ month: r.month, secured: r.secured }));

  return (
    <Page>
      <PageHeader title="Dashboard" subtitle={`Active entity · ${monthName(d.month)} · what needs your attention`}>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowCustomise(true)} data-testid="dashboard-customise"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-sm font-medium border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
            <SlidersHorizontal size={15} /> Customise
          </button>
          <button onClick={() => setCoachReplay((c) => c + 1)} data-testid="dashboard-show-around"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-sm font-medium border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
            <Compass size={15} /> Show me around
          </button>
          {d.tenant_name && (
            <div className="text-right" data-testid="dashboard-org-name">
              <div className="overline text-slate-600">Organisation</div>
              <div className="font-heading font-bold text-lg text-navy truncate max-w-[280px]">{d.tenant_name}</div>
            </div>
          )}
        </div>
      </PageHeader>

      <div className="mb-6" data-testid="dashboard-month-end-bar">
        <MonthEndBar />
      </div>

      <DashboardCoach enabled={d.has_tenant} replay={coachReplay} />

      <JournalsDueModal open={showDue} onClose={() => setShowDue(false)}
        onGoToClose={() => { setShowDue(false); nav("/close"); }} />

      <DashboardCustomise open={showCustomise} onClose={() => setShowCustomise(false)}
        widgets={DASH_WIDGETS} order={layout.order} hidden={layout.hidden}
        onChange={saveLayout} onReset={() => saveLayout(normaliseLayout(null))} />

      {/* Guard alert — release journals the live-register guard auto-cancelled (won't post to Xero) */}
      {d.guard_blocked?.count > 0 && !guardDismissed && (
        <div data-testid="guard-blocked-banner"
          className="flex flex-wrap items-center gap-3 rounded-2xl border border-warn/40 bg-warn/[0.07] px-5 py-3.5 mb-4">
          <Warning weight="fill" className="text-warn shrink-0" size={20} />
          <div className="flex-1 min-w-[220px]">
            <div className="font-heading font-bold text-navy text-sm">
              {d.guard_blocked.count} release journal{d.guard_blocked.count === 1 ? "" : "s"} auto-cancelled by the safety guard
            </div>
            <div className="text-xs text-navy/70">
              These were blocked from posting to Xero because their schedule is no longer a live register line — so your ledger stays in step.
            </div>
          </div>
          <button onClick={() => nav("/audit?filter=autocancelled")} data-testid="guard-blocked-review"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-1.5 rounded-xl border border-warn/50 text-warn hover:bg-warn hover:text-white transition-colors">
            Review <ArrowRight size={15} weight="bold" />
          </button>
          <button onClick={dismissGuard} data-testid="guard-blocked-dismiss" aria-label="Dismiss"
            className="p-1.5 rounded-lg text-navy/40 hover:text-navy hover:bg-navy/5 transition-colors">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Waiting on you — compact Action Inbox summary (shares buildInbox with the Action Inbox) */}
      {(() => {
        const chips = buildInbox(d).filter((c) => c.count > 0);
        const total = chips.reduce((s, c) => s + c.count, 0);
        const next = nextAction(d);
        return (
          <div data-testid="dashboard-inbox-summary"
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white shadow-card px-5 py-3.5 mb-4">
            <Tray weight="fill" className="text-orangeink shrink-0" size={20} />
            <span className="font-heading font-bold text-navy">Waiting on you</span>
            {total === 0 ? (
              <span className="text-sm text-positive font-medium inline-flex items-center gap-1"><CheckCircle weight="fill" size={16} /> All caught up</span>
            ) : (
              <>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-navy text-white tabular">{total}</span>
                <div className="flex flex-wrap gap-2">
                  {chips.map((c) => (
                    <button key={c.key} onClick={() => nav(c.to)} data-testid={`dash-inbox-chip-${c.key}`}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${c.urgent ? "border-negative/40 bg-negative/5 text-negative hover:border-negative" : "border-slate-200 text-navy/75 hover:border-orange hover:text-navy"}`}>
                      <span className={`tabular font-bold ${c.urgent ? "text-negative" : "text-orangeink"}`}>{c.count}</span> {c.short}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="ml-auto flex items-center gap-2">
              {next && (
                <button onClick={startGuided} data-testid="dashboard-start-here"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-orange text-white hover:bg-navy transition-colors">
                  Start here <CaretRight size={14} weight="bold" />
                </button>
              )}
              <button onClick={() => nav("/inbox")} data-testid="dashboard-inbox-open"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-orangeink hover:underline">
                {next ? "Inbox" : "Open Action Inbox"} <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Month-end path */}
      <MonthEndJourney data={d} />

      {/* Learning Centre entry — dismissible for returning users */}
      {learningDismissed ? (
        <button onClick={restoreLearning} data-testid="dashboard-learning-restore"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy/55 hover:text-orangeink mb-4 transition-colors">
          <GraduationCap size={14} /> Show Learning Centre
        </button>
      ) : (
      <div className="relative mb-4">
        <button onClick={() => nav("/learning")} data-testid="dashboard-learning-cta"
          className="group w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl bg-navy text-white shadow-card p-5 text-left hover:shadow-card-hover transition-all">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.18)" }}>
              <GraduationCap size={22} weight="duotone" style={{ color: "#F37A24" }} />
            </div>
            <div>
              <h3 className="font-heading font-bold flex items-center gap-2">
                Learning Centre
                <span className="text-[0.65rem] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "rgba(242,111,33,0.2)", color: "#F9B384" }}>Videos</span>
              </h3>
              <p className="text-white/60 text-sm mt-0.5">Watch narrated video walkthroughs, follow step-by-step guides and take interactive tours — all in one place.</p>
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-2 font-semibold px-5 py-3 rounded-xl text-white transition-transform group-hover:scale-105"
            style={{ background: "#F37A24" }}>
            <PlayCircle size={18} weight="fill" /> Open Learning Centre
          </span>
        </button>
        <button onClick={(e) => { e.stopPropagation(); dismissLearning(); }} data-testid="dashboard-learning-dismiss"
          title="Hide for now" aria-label="Hide Learning Centre"
          className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <X size={15} weight="bold" />
        </button>
      </div>
      )}

      {/* Open review queries (always shown when present) */}
      {d.open_notes_count > 0 && (
        <div className="card-surface p-6 mb-6" data-testid="dashboard-open-notes">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChatCircleText size={17} className="text-orangeink" />
              <h3 className="font-heading font-bold text-navy tracking-tight">Open review queries</h3>
              <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-md bg-orange/10 text-orangeink" data-testid="open-notes-count">{d.open_notes_count}</span>
              {d.mentions_me_count > 0 && (
                <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-md bg-warn/15 text-warn" data-testid="mentions-me-count">
                  <At size={11} weight="bold" /> {d.mentions_me_count} for you
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {(d.open_notes || []).map((n) => (
              <button key={n.id} onClick={() => nav(n.link)} data-testid={`open-note-${n.id}`}
                className="w-full text-left flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-orange/40 hover:bg-orange/[0.03] transition-colors group">
                <span className={`shrink-0 mt-0.5 text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md ${n.entity_type === "journal" ? "bg-navy/[0.06] text-navy/70" : "bg-orange/10 text-orangeink"}`}>
                  {n.entity_type === "journal" ? "Journal" : "Schedule"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-navy truncate">{n.label}</span>
                    {n.mentions_me && <span className="inline-flex items-center gap-0.5 text-[0.6rem] font-semibold text-warn shrink-0"><At size={10} weight="bold" /> you</span>}
                  </div>
                  <div className="text-xs text-navy/70 truncate">{n.author_name}: {n.body}</div>
                </div>
                <ArrowRight size={14} className="text-slate-300 group-hover:text-orangeink transition-colors shrink-0 mt-1" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Customisable widget area — order & visibility saved per user */}
      {(() => {
        const widgets = {
          "action-tiles": (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-actions">
              <ActionTile id="tile-unscheduled" icon={ListChecks} tone="orange" delay={0}
                value={d.unscheduled_count} label="Unscheduled items"
                sub={d.auto_scheduled ? `${d.auto_scheduled} auto-scheduled by rules` : "awaiting a treatment"} onClick={() => nav("/unscheduled")} />
              <ActionTile id="tile-journals-due" icon={CalendarCheck} tone="navy" delay={0.05}
                value={d.journals_due.count} label="Journals due this month"
                sub={money(d.journals_due.amount)} onClick={() => setShowDue(true)} />
              <ActionTile id="tile-overdue" icon={Warning} tone={d.journals_overdue.count ? "negative" : "navy"} delay={0.1}
                value={d.journals_overdue.count} label="Overdue journals"
                sub={d.journals_overdue.count ? money(d.journals_overdue.amount) : "all on track"} onClick={() => nav("/close")} />
              <ActionTile id="tile-approvals" icon={ShieldCheck} tone={d.approvals_waiting ? "warn" : "navy"} delay={0.15}
                value={d.approvals_waiting} label="Approvals waiting"
                sub={!d.approval_policy?.required
                  ? "approval not enabled"
                  : (d.approvals_waiting
                      ? "needs a second pair of eyes"
                      : (d.approval_policy?.scope === "manual_only" ? "nothing awaiting · manual posts only" : "nothing awaiting approval"))}
                onClick={() => nav("/approvals")} />
            </div>
          ),
          "status-strip": (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StripCard id="strip-recon" icon={Scales} onClick={() => nav("/reconciliation")}
                dot={reconOk ? "bg-positive" : recon.variances > 0 ? "bg-warn" : "bg-slate-300"}
                title="Reconciliation"
                sub={recon.total ? `${recon.reconciled}/${recon.total} codes in balance${recon.variances ? ` · ${recon.variances} variance(s)` : ""}` : "no data"} />
              <StripCard id="strip-sync" icon={ArrowsClockwise} onClick={() => nav("/unscheduled")}
                title="Last synced" sub={ago(d.last_synced_at)} />
              <StripCard id="strip-recurring" icon={Sparkle} iconClass="text-orangeink" onClick={() => nav("/accruals")}
                title="Recurring accruals"
                titleAttr={accuracyMeta?.count ? `Accuracy = 100% minus the mean absolute error between predicted and actual amounts, back-tested across ${accuracyMeta.count} posted recurring accrual${accuracyMeta.count === 1 ? "" : "s"}.` : "Accuracy appears once there are enough posted recurring accruals to back-test."}
                sub={`${d.recurring_suggestions} suggestion(s)${accuracy != null ? ` · ${accuracy}% accuracy (${accuracyMeta?.count || 0} sampled)` : ""}`} />
            </div>
          ),
          "position-type": (
            <div data-testid="dashboard-position-widget">
              <div className="overline text-slate-600 mb-3">Position by type</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-position">
                {Object.entries(TYPE_META).map(([key, m], i) => {
                  const t = d.by_type[key] || { original: 0, released: 0, remaining: 0 };
                  const pct = t.original > 0 ? Math.min(100, Math.round((t.released / t.original) * 100)) : 0;
                  const Icon = m.icon;
                  return (
                    <motion.button key={key} onClick={() => nav(m.to)} data-testid={`position-${key}`}
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 * i }}
                      whileHover={{ y: -3 }}
                      className="text-left card-interactive p-5 group">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm font-medium text-navy/70"><Icon size={16} className="text-orangeink" /> {m.label}</span>
                        <ArrowRight size={14} className="text-slate-300 group-hover:text-orangeink transition-colors" />
                      </div>
                      <div className="font-heading font-extrabold text-2xl text-navy tracking-tight mt-3 tabular">{money(t.remaining)}</div>
                      <div className="text-xs text-slate-600 mt-0.5">remaining of {money(t.original)}</div>
                      <div className="h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden"><div className="h-full bg-orange rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ),
          "revenue-secured": (
            <div className="card-surface p-6" data-testid="dashboard-forecast">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2"><Target size={17} className="text-orangeink" /><h3 className="font-heading font-bold text-navy tracking-tight">Revenue secured</h3></div>
                <button onClick={() => nav("/deferred-reports")} className="text-xs text-orangeink flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="forecast-link">Full forecast <ArrowRight size={12} /></button>
              </div>
              <div className="font-heading font-extrabold text-3xl text-navy tabular">{money(d.secured_total)}</div>
              <div className="text-xs text-slate-600 mb-4">deferred income not yet recognised</div>
              <div className="h-32">
                {chartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-600">No forecast data</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                      <XAxis dataKey="month" tickFormatter={(m) => m.slice(5)} axisLine={false} tickLine={false}
                        tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <Tooltip cursor={{ fill: "rgba(46,163,253,0.08)" }}
                        contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12, boxShadow: "0 8px 24px -6px rgba(11,26,47,0.12)" }}
                        formatter={(v) => [money(v), "Secured"]} labelFormatter={(l) => monthName(l)} />
                      <Bar dataKey="secured" radius={[4, 4, 0, 0]} maxBarSize={32}>
                        {chartData.map((_, i) => <Cell key={i} fill="#F37A24" fillOpacity={0.85} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          ),
          "recent-activity": (
            <div className="card-surface p-6" data-testid="dashboard-activity">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><ClockCounterClockwise size={17} className="text-navy/70" /><h3 className="font-heading font-bold text-navy tracking-tight">Recent activity</h3></div>
                <button onClick={() => nav("/audit")} className="text-xs text-orangeink flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="activity-link">Audit history <ArrowRight size={12} /></button>
              </div>
              <div className="space-y-3">
                {(d.recent_activity || []).length === 0 && <div className="text-sm text-slate-600">No activity yet.</div>}
                {(d.recent_activity || []).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 text-sm">
                    <span className="text-[0.6rem] font-mono uppercase tracking-wider text-slate-600 mt-0.5 shrink-0 w-28 truncate">{a.action}</span>
                    <span className="text-navy/75 truncate">{a.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          ),
        };
        const visible = layout.order.filter((id) => !layout.hidden.includes(id));
        if (visible.length === 0) {
          return <div className="card-surface p-8 text-center text-navy/55 text-sm" data-testid="dashboard-empty">All cards are hidden. Use <span className="font-semibold">Customise</span> to show them again.</div>;
        }
        return visible.map((id) => (
          <div key={id} className="mb-6" data-testid={`dash-widget-${id}`}>{widgets[id]}</div>
        ));
      })()}
    </Page>
  );
}
